import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePovinnosti } from "./hooks/usePovinnosti";
import { useEvidenciaPrace } from "./context/EvidenciaPraceContext";
import PovinnostiForm from "./components/PovinnostiForm";
import TasksTable from "./components/TasksTable";
import PovinnostiMapa from "./components/PovinnostiMapa";
import PovinnostiKalendar from "./components/PovinnostiKalendar";
import MiniUlohy from "./components/MiniUlohy";

const CATEGORY_KEYWORDS = {
  dph: ['dph'],
  dzp: ['daň z príjmov', 'dani z príjmov', 'dan z prijmov', 'dzp'],

  mzdy: ['mzd'],
};

const TABS = [
  { id: "zoznam", label: "Zoznam" },
  { id: "mapa", label: "Mapa" },
  { id: "kalendar", label: "Kalendár" },
];

export default function Povinnosti(props) {
  const povinnosti = usePovinnosti();
  const [activeTab, setActiveTab] = useState("zoznam");
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [miniUlohyCollapsed, setMiniUlohyCollapsed] = useState(false);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.altKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        setActiveTab("zoznam");
        setTimeout(() => povinnosti.povinnostInputRef?.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [povinnosti.povinnostInputRef]);
  const currentYear = new Date().getFullYear().toString();
  const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const navigate = useNavigate();
  const { setPendingEntry } = useEvidenciaPrace();

  const handleSendToEvidence = (task) => {
    setPendingEntry({
      companyName: task.company || '',
      description: task.text || '',
      minutes: task.estimatedTime || 0,
      date: new Date().toISOString().split('T')[0],
      workPeriodFrom: task.workPeriod || '',
      workPeriodTo: '',
      unitPrice: 0,
      actualInvoicePrice: 0,
      note: ''
    });
    navigate('/evidencia-prace');
  };

  const handleSendToKalkulacka = (task) => {
    navigate('/kalkulacka', { state: { prefill: {
      company: task.company || '',
      date: new Date().toISOString().split('T')[0],
      accountingType: task.accountingType || '',
    } } });
  };

  const mergeFilteredWithAllTasks = (filteredTasks) => {
    const allTasks = povinnosti.tasks || [];
    const incoming = Array.isArray(filteredTasks)
      ? filteredTasks
      : filteredTasks && typeof filteredTasks === 'object'
        ? [filteredTasks]
        : [];

    const filteredMap = new Map(incoming.map(t => [t.id, t]));
    const mergedExisting = allTasks.map(task => filteredMap.get(task.id) || task);
    const existingIds = new Set(allTasks.map(task => task.id));
    const newTasks = incoming.filter(task => !existingIds.has(task.id));
    return [...mergedExisting, ...newTasks];
  };

  const saveTasksWithAllItems = async (newFilteredTasks) => {
    const merged = mergeFilteredWithAllTasks(newFilteredTasks);
    return await povinnosti.handleSaveTasks(povinnosti.userPin, merged);
  };

  return (
    <>
      {!povinnosti.isAuthenticated ? (
        <div style={{ maxWidth: 400, margin: "3rem auto", background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: 32, textAlign: "center" }}>
          <h2 style={{ marginBottom: 16 }}>{povinnosti.registerMode ? "Registrácia" : "Prihlásenie"}</h2>
          <form onSubmit={e => { e.preventDefault(); povinnosti.handlePinLogin(povinnosti.pinInput); }}>
            <input
              type="password"
              value={povinnosti.pinInput}
              onChange={e => povinnosti.setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="PIN"
              style={{ fontSize: 24, textAlign: "center", width: 180, padding: 8, borderRadius: 6 }}
              maxLength={4}
              autoFocus
            />
            <div style={{ margin: "12px 0" }}>
              <button type="submit">Vstúpiť</button>
            </div>
          </form>
        </div>
      ) : (
        <div style={{ paddingBottom: "5rem" }}>
          {/* Záložky */}
          <div style={{ display: "flex", gap: 0, marginBottom: 18, borderBottom: "2px solid #444" }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "7px 22px",
                  fontSize: 14,
                  fontWeight: activeTab === tab.id ? "bold" : "normal",
                  background: activeTab === tab.id ? "#fff" : "transparent",
                  color: activeTab === tab.id ? "#111" : "#bbb",
                  border: "none",
                  borderBottom: activeTab === tab.id ? "2px solid #fff" : "none",
                  marginBottom: "-2px",
                  cursor: "pointer",
                  borderRadius: "4px 4px 0 0",
                  transition: "color 0.15s",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "zoznam" && (
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
          <PovinnostiForm
            formText={povinnosti.formText}
            setFormText={povinnosti.setFormText}
            autocompleteText={povinnosti.autocompleteText || ""}
            setAutocompleteText={povinnosti.setAutocompleteText}
            uniqueTaskTemplatesFiltered={povinnosti.uniqueTaskTemplatesFiltered || []}
            showTaskSuggestions={povinnosti.showTaskSuggestions}
            setShowTaskSuggestions={povinnosti.setShowTaskSuggestions}
            povinnostInputRef={povinnosti.povinnostInputRef}
            company={povinnosti.company}
            setCompany={povinnosti.setCompany}
            deadline={povinnosti.deadline}
            setDeadline={povinnosti.setDeadline}
            deadlineTime={povinnosti.deadlineTime}
            setDeadlineTime={povinnosti.setDeadlineTime}
            repeatInterval={povinnosti.repeatInterval}
            setRepeatInterval={povinnosti.setRepeatInterval}
            estimatedTime={povinnosti.estimatedTime}
            setEstimatedTime={povinnosti.setEstimatedTime}
            subtasksInput={povinnosti.subtasksInput}
            setSubtasksInput={povinnosti.setSubtasksInput}
            formFiles={povinnosti.formFiles || []}
            setFormFiles={povinnosti.setFormFiles}
            companyList={povinnosti.companyList}
            addToCompanyList={povinnosti.addToCompanyList}
            taskList={povinnosti.taskList}
            addToTaskList={povinnosti.addToTaskList}
            formPeriod={povinnosti.formPeriod}
            setFormPeriod={povinnosti.setFormPeriod}
            handleAddTasks={async (e) => {
              e.preventDefault();
              const tasksArr = povinnosti.formText.split(',').map(t => t.trim()).filter(Boolean);
              if (tasksArr.length === 0) return;
              const newTasks = tasksArr.map(text => ({
                id: Date.now().toString() + Math.random().toString(36).slice(2),
                text,
                company: povinnosti.company,
                deadline: povinnosti.deadline,
                deadlineTime: povinnosti.deadlineTime || "",
                interval: povinnosti.repeatInterval,
                estimatedTime: povinnosti.estimatedTime,
                workPeriod: povinnosti.formPeriod || "",
                subtasks: povinnosti.subtasksInput
                  ? povinnosti.subtasksInput.split('\n').map(st => ({ id: Date.now().toString() + Math.random().toString(36).slice(2), text: st, completed: false }))
                  : [],
                completed: false
              }));
              await povinnosti.handleAddTasks(newTasks);
              povinnosti.setFormText("");
              povinnosti.setSubtasksInput("");
              const now = new Date();
              const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              povinnosti.setFormPeriod(`${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`);
            }}
          />

          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {[['all', 'Všetky'], ['dph', 'Iba DPH'], ['dzp', 'Iba DZP'], ['mzdy', 'Iba mzdy']].map(([val, label]) => (
              <button
                key={val}
                onClick={() => setCategoryFilter(val)}
                style={{ padding: '4px 12px', borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer', fontWeight: categoryFilter === val ? 700 : 400, background: categoryFilter === val ? '#059669' : 'rgba(255,255,255,0.08)', color: categoryFilter === val ? '#fff' : '#ccc', fontSize: 13 }}
              >{label}</button>
            ))}
            <div style={{ width: 1, background: '#444', margin: '0 4px' }} />
            <button
              onClick={() => setYearFilter(y => y === 'year' ? 'all' : 'year')}
              style={{ padding: '4px 12px', borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer', fontWeight: yearFilter === 'year' ? 700 : 400, background: yearFilter === 'year' ? '#0369a1' : 'rgba(255,255,255,0.08)', color: yearFilter === 'year' ? '#fff' : '#ccc', fontSize: 13 }}
            >Tento rok</button>
            <button
              onClick={() => setYearFilter(y => y === 'month' ? 'all' : 'month')}
              style={{ padding: '4px 12px', borderRadius: 5, border: '1px solid #ccc', cursor: 'pointer', fontWeight: yearFilter === 'month' ? 700 : 400, background: yearFilter === 'month' ? '#7c3aed' : 'rgba(255,255,255,0.08)', color: yearFilter === 'month' ? '#fff' : '#ccc', fontSize: 13 }}
            >Tento mesiac</button>
          </div>

          <TasksTable
            tasks={(() => {
              let result = povinnosti.tasks || [];
              if (categoryFilter !== 'all') {
                result = result.filter(t => {
                  const text = (t.text || '').toLowerCase();
                  return CATEGORY_KEYWORDS[categoryFilter].some(kw => text.includes(kw));
                });
              }
              if (yearFilter === 'year') {
                result = result.filter(t =>
                  (t.deadline || '').startsWith(currentYear)
                );
              }
              if (yearFilter === 'month') {
                result = result.filter(t =>
                  (t.deadline || '').startsWith(currentMonth)
                );
              }
              return result;
            })()}
            setTasks={(newFilteredTasks) => {
              const merged = mergeFilteredWithAllTasks(newFilteredTasks);
              povinnosti.setTasks(merged);
              povinnosti.handleSaveTasks(povinnosti.userPin, merged);
            }}
            loading={povinnosti.loading}
            error={povinnosti.error}
            userPin={povinnosti.userPin}
            saveTasks={saveTasksWithAllItems}
            editId={povinnosti.editId}
            setEditId={povinnosti.setEditId}
            editValues={povinnosti.editValues}
            setEditValues={povinnosti.setEditValues}
            saveEdit={povinnosti.saveEdit}
            cancelEdit={povinnosti.cancelEdit}
            formatDateDMY={povinnosti.formatDateDMY}
            companyList={povinnosti.companyList}
            addToCompanyList={povinnosti.addToCompanyList}
            taskList={povinnosti.taskList}
            addToTaskList={povinnosti.addToTaskList}
            onSendToEvidence={handleSendToEvidence}
            onSendToKalkulacka={handleSendToKalkulacka}
          />
            </div>
            <MiniUlohy
              collapsed={miniUlohyCollapsed}
              onToggle={() => setMiniUlohyCollapsed(v => !v)}
              userPin={povinnosti.userPin}
            />
            </div>
          )}

          {activeTab === "mapa" && (
            <PovinnostiMapa
              tasks={povinnosti.tasks}
              formatDateDMY={povinnosti.formatDateDMY}
            />
          )}

          {activeTab === "kalendar" && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <a
                  href={`/php/calendar_feed.php?pin=${povinnosti.userPin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: 12,
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: '1px solid #cbd5e1',
                    background: '#f8fafc',
                    color: '#0f172a',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  title="Otvoriť externý iCal feed povinností"
                >
                  📅 iCal feed
                </a>
              </div>
              <PovinnostiKalendar
                tasks={povinnosti.tasks}
                setTasks={povinnosti.setTasks}
                saveTasks={povinnosti.handleSaveTasks}
                userPin={povinnosti.userPin}
                companyList={povinnosti.companyList}
                addToCompanyList={povinnosti.addToCompanyList}
                taskList={povinnosti.taskList}
                addToTaskList={povinnosti.addToTaskList}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}